import { ChangeEvent, useState } from 'react';

import { FormControl, Grid, InputLabel, Paper, Select, TextField, makeStyles } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { useAdminGetCategories } from '../../../categories/hooks/admin-get-categories.hook';
import { useAdminGetSubCategories } from '../../../categories/hooks/admin-get-sub-categories.hook';
import { useUpdateSubCategory } from '../../../categories/hooks/update-sub-category.hook';
import { useUpdateSubCategoriesPositions } from '../../../categories/hooks/update-sub-categories-positions.hook';
import { useArchiveSubCategory } from '../../../categories/hooks/archive-sub-category.hook';
import { useRestoreSubCategory } from '../../../categories/hooks/restore-sub-category.hook';
import { Category } from '../../../categories/types/category.type';
import { SubCategory } from '../../../categories/types/sub-category.type';
import { UpdateSubCategoryRequestDTO } from '../../../categories/types/dtos/update-sub-category.dto';
import {
  ENTITY_STATUS_FILTERS,
  ENTITY_STATUS_FILTER_LABELS,
  EntityStatusFilter,
  toEntityListFilters,
} from '../../../shared/constants/entity-status-filter.constants';
import { DEFAULT_ROWS_PER_PAGE } from '../../../shared/constants/table.constants';
import { ConfirmActionDialog } from '../../../shared/components/Modals/ConfirmActionDialog.component';
import SubCategoriesTable from '../../components/Tables/SubCategoriesTable.component';
import CreateSubCategoryForm from '../../components/Forms/CreateSubCategoryForm.component';
import { EditSubCategoryDialog } from '../../components/Modals/EditSubCategoryDialog.component';

const STATUS_FILTER_INPUT_ID = 'sub-categories-status-filter';

type ArchiveAction = 'archive' | 'restore';

type DialogState =
  | { kind: 'none' }
  | { kind: 'edit'; subCategory: SubCategory }
  | { kind: 'confirm'; action: ArchiveAction; subCategory: SubCategory };

type ConfirmDialogContent = {
  title: string;
  describe: (subCategoryName: string) => string;
  confirmLabel: string;
};

const CONFIRM_DIALOG_CONTENT: Record<ArchiveAction, ConfirmDialogContent> = {
  archive: {
    title: 'Archiver la sous-rubrique',
    describe: (subCategoryName) =>
      `La sous-rubrique « ${subCategoryName} » sera retirée des listes et du site, et pourra être restaurée.`,
    confirmLabel: 'Archiver',
  },
  restore: {
    title: 'Restaurer la sous-rubrique',
    describe: (subCategoryName) =>
      `La sous-rubrique « ${subCategoryName} » retrouvera sa place en fin de rubrique, inactive.`,
    confirmLabel: 'Restaurer',
  },
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },

    '& h4': {
      margin: '0 0 0.5rem 0',
      opacity: 0.7,
    },
  },

  listColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',
  },

  createPanel: {
    padding: '1rem 0.75rem',
  },

  filters: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '1rem',
  },
});

export default function AdminSubCategoriesListView() {
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [statusFilter, setStatusFilter] = useState<EntityStatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<Category | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });

  const { categories } = useAdminGetCategories();
  const { categories: archivedCategories } = useAdminGetCategories({ filters: { archived: true } });
  const { isLoading, isRefetching, subCategories, totalCount, refetch } = useAdminGetSubCategories({
    page: page + 1,
    limit: rowsPerPage,
    sortBy: 'position',
    sortDirection: 'asc',
    filters: {
      category: categoryFilter?.id,
      ...toEntityListFilters(statusFilter),
    },
  });

  const { updatePositions } = useUpdateSubCategoriesPositions();
  const { updateSubCategory, isSubmitting: isUpdating } = useUpdateSubCategory();
  const { archiveSubCategory, isSubmitting: isArchiving } = useArchiveSubCategory();
  const { restoreSubCategory, isSubmitting: isRestoring } = useRestoreSubCategory();

  const isDragEnabled = Boolean(categoryFilter) && statusFilter === 'all';
  const categoryFilterOptions = [...categories].sort((first, second) => first.name.localeCompare(second.name));

  const closeDialog = () => setDialog({ kind: 'none' });

  const handleToggleActive = async (subCategoryId: string, isActive: boolean): Promise<boolean> => {
    const result = await updateSubCategory({ subCategoryId, subCategoryData: { isActive } });
    return result !== null;
  };

  const handlePositionsChange = (updatedSubCategories: { id: string; position: number }[]) => {
    updatePositions(updatedSubCategories);
  };

  const handleEditSubmit = async (subCategoryData: UpdateSubCategoryRequestDTO) => {
    if (dialog.kind !== 'edit') return;

    const updatedSubCategory = await updateSubCategory({ subCategoryId: dialog.subCategory.id, subCategoryData });
    if (!updatedSubCategory) return;

    closeDialog();
    await refetch();
  };

  const handleConfirm = async () => {
    if (dialog.kind !== 'confirm') return;

    const { action, subCategory } = dialog;
    const result =
      action === 'archive' ? await archiveSubCategory(subCategory.id) : await restoreSubCategory(subCategory.id);
    if (!result) return;

    closeDialog();
    await refetch();
  };

  const handleStatusFilterChange = (value: EntityStatusFilter) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handleCategoryFilterChange = (value: Category | null) => {
    setCategoryFilter(value);
    setPage(0);
  };

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <section className={classes.root}>
      <h2>Sous-rubriques</h2>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8} className={classes.listColumn}>
          <section className={classes.filters}>
            <Autocomplete
              style={{ minWidth: '200px' }}
              size="small"
              value={categoryFilter}
              options={categoryFilterOptions}
              groupBy={(option) => option.name[0].toUpperCase()}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, selected) => option.id === selected.id}
              onChange={(event, selectedOption: Category | null) => handleCategoryFilterChange(selectedOption)}
              renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="Rubrique" />}
            />
            <FormControl variant="outlined" size="small">
              <InputLabel htmlFor={STATUS_FILTER_INPUT_ID}>Statut</InputLabel>
              <Select
                native
                label="Statut"
                value={statusFilter}
                inputProps={{ id: STATUS_FILTER_INPUT_ID }}
                onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                  handleStatusFilterChange(event.target.value as EntityStatusFilter)
                }
              >
                {ENTITY_STATUS_FILTERS.map((status) => (
                  <option key={status} value={status}>
                    {ENTITY_STATUS_FILTER_LABELS[status]}
                  </option>
                ))}
              </Select>
            </FormControl>
          </section>
          <SubCategoriesTable
            subCategories={subCategories}
            categories={[...categories, ...archivedCategories]}
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            isLoading={isLoading || isRefetching}
            isDragEnabled={isDragEnabled}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onToggleActive={handleToggleActive}
            onPositionsChange={handlePositionsChange}
            onEdit={(subCategory) => setDialog({ kind: 'edit', subCategory })}
            onArchive={(subCategory) => setDialog({ kind: 'confirm', action: 'archive', subCategory })}
            onRestore={(subCategory) => setDialog({ kind: 'confirm', action: 'restore', subCategory })}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <h4>Créer une sous-rubrique</h4>
          <Paper className={classes.createPanel}>
            <CreateSubCategoryForm onSubCategoryCreated={refetch} />
          </Paper>
        </Grid>
      </Grid>
      {dialog.kind === 'edit' && (
        <EditSubCategoryDialog
          open
          subCategory={dialog.subCategory}
          isSubmitting={isUpdating}
          onSubmit={handleEditSubmit}
          onClose={closeDialog}
        />
      )}
      {dialog.kind === 'confirm' && (
        <ConfirmActionDialog
          open
          title={CONFIRM_DIALOG_CONTENT[dialog.action].title}
          description={CONFIRM_DIALOG_CONTENT[dialog.action].describe(dialog.subCategory.name)}
          confirmLabel={CONFIRM_DIALOG_CONTENT[dialog.action].confirmLabel}
          isSubmitting={isArchiving || isRestoring}
          onConfirm={handleConfirm}
          onClose={closeDialog}
        />
      )}
    </section>
  );
}
